import * as Sentry from "@sentry/react"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { RoutePending } from "./components/shared/loading/route-pending"
import { ThemeProvider } from "./components/shared/theme-provider"
import "./index.css"
import { queryClient, type RouterContext } from "./routes/__root"
import { routeTree } from "./routeTree.gen"

if (import.meta.env.VITE_SENTRY_DSN) {
	Sentry.init({
		dsn: import.meta.env.VITE_SENTRY_DSN,
		environment: import.meta.env.MODE,
		integrations: [Sentry.browserTracingIntegration()],
		tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
		enabled: import.meta.env.PROD,
	})
}

const router = createRouter({
	routeTree,
	context: { auth: null, queryClient } satisfies RouterContext,
	defaultPreload: "intent",
	defaultPendingComponent: RoutePending,
	scrollRestoration: true,
})

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router
	}
}

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<ThemeProvider>
			<Sentry.ErrorBoundary
				fallback={
					<div className="flex min-h-screen items-center justify-center bg-background">
						<div className="w-full max-w-sm border border-border bg-card p-8 text-center shadow-sm">
							<h1 className="text-xl font-semibold">Something went wrong</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								An unexpected error occurred. Please refresh the page or contact
								support if the problem persists.
							</p>
							<button
								type="button"
								onClick={() => window.location.reload()}
								className="mt-4 w-full cursor-pointer bg-foreground py-2 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
							>
								Refresh Page
							</button>
						</div>
					</div>
				}
			>
				<RouterProvider router={router} />
			</Sentry.ErrorBoundary>
		</ThemeProvider>
	</StrictMode>,
)
